import React, { useEffect, useState } from 'react';
import { collection, getDocs, updateDoc, doc, query, where, orderBy, Timestamp, deleteDoc, writeBatch } from 'firebase/firestore';
import { db, auth, updateAlatGelasStockOnLoanApproval, updateAlatGelasStockOnReturn, restoreAlatGelasStockOnDelete } from '../../firebase';
import {
  Box, Paper, Typography, Tabs, Tab, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, TextField, IconButton, List, ListItem,
  ListItemText, Collapse, Alert, ToggleButton, ToggleButtonGroup, Table, TableBody, TableCell, TableHead, TableRow, CircularProgress
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import './PersetujuanPermintaanAlat.css';

// Helper to format Firestore Timestamp to readable date string
const formatDate = (timestamp) => {
  if (timestamp instanceof Timestamp) {
    return timestamp.toDate().toLocaleDateString('id-ID');
  }
  return typeof timestamp === 'string' ? timestamp : 'N/A';
};

// Helper function to group requests by submission ID
const groupRequestsBySubmission = (requests) => {
  const grouped = {};
  requests.forEach(req => {
    if (!req.submissionId) {
      // Handle legacy requests without submissionId (group by userId as before)
      if (!grouped[req.userId]) {
        grouped[req.userId] = {
          namaUser: req.namaUser,
          userNim: req.userNim,
          submissionId: null,
          tanggalPermintaan: req.tanggalPermintaan,
          requests: []
        };
      }
      grouped[req.userId].requests.push(req);
    } else {
      // Group by submissionId for new requests
      if (!grouped[req.submissionId]) {
        grouped[req.submissionId] = {
          namaUser: req.namaUser,
          userNim: req.userNim,
          submissionId: req.submissionId,
          tanggalPermintaan: req.tanggalPermintaan,
          requests: []
        };
      }
      grouped[req.submissionId].requests.push(req);
    }
  });
  
  // Sort groups by date (newest first)
  const sortedGrouped = {};
  Object.keys(grouped)
    .sort((a, b) => {
      const timeA = grouped[a].tanggalPermintaan?.seconds || 0;
      const timeB = grouped[b].tanggalPermintaan?.seconds || 0;
      return timeB - timeA;
    })
    .forEach(key => {
      sortedGrouped[key] = grouped[key];
    });
    
  return sortedGrouped;
};

const PersetujuanPermintaanAlat = () => {
  const [tabValue, setTabValue] = useState(0);
  const [expandedSubmission, setExpandedSubmission] = useState(null);
  const [success, setSuccess] = useState('');
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnQuantities, setReturnQuantities] = useState({});
  const [returnRequests, setReturnRequests] = useState([]); // Array of requests from same student
  const [instrumentRequests, setInstrumentRequests] = useState([]);
  const [glasswareRequests, setGlasswareRequests] = useState([]);
  const [unreturnedRequests, setUnreturnedRequests] = useState([]); // Alat yang belum dikembalikan
  const [loading, setLoading] = useState(false);
  const [loadingUnreturned, setLoadingUnreturned] = useState(false); // Loading state untuk unreturned
  const [error, setError] = useState('');
  const [updatingId, setUpdatingId] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [returnDetails, setReturnDetails] = useState(null);
  const [studentDetailsOpen, setStudentDetailsOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [viewMode, setViewMode] = useState('today'); // 'today' or 'history'
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]); // Format: YYYY-MM-DD

  // Fetch all requests - optimized to only fetch urgent statuses from selected date
  const fetchRequests = async (dateFilter = null) => {
    setLoading(true);
    setError('');
    try {
      let dateStart, dateEnd;
      
      if (viewMode === 'today' || !dateFilter) {
        // Get today's date range (start and end of day)
        const today = new Date();
        dateStart = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
        dateEnd = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999);
      } else if (viewMode === 'history' && dateFilter) {
        // Use selected date
        const selected = new Date(dateFilter);
        dateStart = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), 0, 0, 0, 0);
        dateEnd = new Date(selected.getFullYear(), selected.getMonth(), selected.getDate(), 23, 59, 59, 999);
      } else {
        // No date filter - fetch all (for history view)
        dateStart = null;
        dateEnd = null;
      }
      
      const requestsRef = collection(db, 'permintaan_alat');
      let q;
      
      if (dateStart && dateEnd) {
        const dateStartTs = Timestamp.fromDate(dateStart);
        const dateEndTs = Timestamp.fromDate(dateEnd);
        q = query(
          requestsRef, 
          where('tanggalPermintaan', '>=', dateStartTs),
          where('tanggalPermintaan', '<=', dateEndTs)
        );
      } else {
        // Fetch all without date filter (for full history)
        q = query(requestsRef);
      }

      const querySnapshot = await getDocs(q);
      let data = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      
      // Filter by status client-side to avoid composite index requirement
      // For history view, include all statuses including returned and cancelled
      const urgentStatuses = viewMode === 'today' 
        ? ['pending', 'dosen_approved', 'approved', 'pending_return', 'dosen_rejected', 'admin_approved', 'admin_rejected']
        : ['pending', 'dosen_approved', 'approved', 'pending_return', 'returned', 'cancelled', 'dosen_rejected', 'admin_approved', 'admin_rejected'];
      data = data.filter(req => {
        if (!urgentStatuses.includes(req.status)) return false;
        
        // For 'approved' status, show all approved requests (they are still borrowing)
        // The return button will only appear for pending_return or approved with partial return
        // But the request itself should still be visible in the list
        return true;
      });

      const statusOrder = { 
        pending: 1,
        dosen_approved: 2,
        pending_return: 3,
        approved: 4,
        returned: 5,
        cancelled: 6,
        dosen_rejected: 7,
        admin_approved: 8,
        admin_rejected: 9
      };
      data.sort((a, b) => {
        const orderA = statusOrder[a.status] || 99;
        const orderB = statusOrder[b.status] || 99;
        if (orderA !== orderB) return orderA - orderB;
        return (b.tanggalPermintaan?.seconds || 0) - (a.tanggalPermintaan?.seconds || 0);
      });

      const instruments = data.filter(req => req.jenisAlat === 'instrumen');
      const glassware = data.filter(req => req.jenisAlat === 'alat_gelas');

      setInstrumentRequests(instruments);
      setGlasswareRequests(glassware);
    } catch (err) {
      console.error("Error fetching tool requests:", err);
      setError('Gagal memuat data permintaan alat.');
    } finally {
      setLoading(false);
    }
  };

  // Handle status update (Approve/Reject)
  const handleUpdateRequestStatus = async (id, newStatus) => {
    setUpdatingId(id);
    setError('');
    try {
      const requestRef = doc(db, 'permintaan_alat', id);
      const requestToUpdate = [...instrumentRequests, ...glasswareRequests].find(req => req.id === id);

      if (!requestToUpdate) {
        throw new Error("Detail permintaan tidak ditemukan.");
      }

      const updateData = {
        status: newStatus === 'approved' ? 'approved' : 
               newStatus === 'rejected' ? 'rejected' : 
               newStatus,
        tanggalPersetujuanAdmin: Timestamp.now()
      };

      // Create approval history entry
      const approvalHistoryEntry = {
        approver: {
          id: auth.currentUser.uid,
          name: 'Admin',
          role: 'admin'
        },
        action: newStatus === 'approved' ? 'approved' : 'rejected',
        timestamp: Timestamp.now()
      };

      // Update document with new status and append to approval history
      updateData.approvalHistory = requestToUpdate.approvalHistory 
        ? [...requestToUpdate.approvalHistory, approvalHistoryEntry]
        : [approvalHistoryEntry];

      await updateDoc(requestRef, updateData);

      if (newStatus === 'approved' && requestToUpdate.jenisAlat === 'alat_gelas') {
        try {
          await updateAlatGelasStockOnLoanApproval(
            requestToUpdate.namaAlat,
            requestToUpdate.jumlah
          );
        } catch (stockError) {
          setError(`Status permintaan ${id} diperbarui, tetapi gagal mengurangi stok alat gelas: ${stockError.message}`);
          setUpdatingId(null);
          return;
        }
      }

      fetchRequests();
    } catch (err) {
      console.error(`Error updating request ${id}:`, err);
      setError(`Gagal memperbarui status permintaan ${id}: ${err.message}`);
    } finally {
      setUpdatingId(null);
    }
  };

  // Handle deleting a request
  const handleDeleteRequest = async (id) => {
    if (!window.confirm('Apakah Anda yakin ingin menghapus permintaan ini secara permanen?')) {
      return;
    }
    setUpdatingId(id);
    setError('');
    try {
      // Get the request data before deleting
      const requestRef = doc(db, 'permintaan_alat', id);
      const requestToDelete = [...instrumentRequests, ...glasswareRequests].find(req => req.id === id);
      
      if (!requestToDelete) {
        throw new Error("Detail permintaan tidak ditemukan.");
      }
      
      // If it's an approved glassware item, restore the stock
      if (requestToDelete.jenisAlat === 'alat_gelas' && 
          (requestToDelete.status === 'approved' || requestToDelete.status === 'pending_return')) {
        try {
          await restoreAlatGelasStockOnDelete(
            requestToDelete.namaAlat,
            requestToDelete.jumlah
          );
        } catch (stockError) {
          console.error(`Error restoring stock for ${requestToDelete.namaAlat}:`, stockError);
          setError(`Gagal mengembalikan stok alat gelas: ${stockError.message}`);
          setUpdatingId(null);
          return;
        }
      }
      
      // Delete the request
      await deleteDoc(requestRef);
      fetchRequests();
    } catch (err) {
      console.error(`Error deleting request ${id}:`, err);
      setError(`Gagal menghapus permintaan ${id}: ${err.message}`);
    } finally {
      setUpdatingId(null);
    }
  };

  // Fetch all unreturned alat gelas (no date filter)
  const fetchUnreturnedRequests = async () => {
    setLoadingUnreturned(true);
    setError('');
    try {
      const requestsRef = collection(db, 'permintaan_alat');
      // Fetch all requests without date filter
      const q = query(requestsRef);
      
      const querySnapshot = await getDocs(q);
      let data = querySnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }));
      
      // Filter only alat gelas that haven't been fully returned
      const unreturned = data.filter(req => {
        // Only alat gelas
        if (req.jenisAlat !== 'alat_gelas') return false;
        
        // Include pending_return (already submitted for return)
        if (req.status === 'pending_return') return true;
        
        // Include approved/admin_approved that haven't been fully returned
        if (req.status === 'approved' || req.status === 'admin_approved') {
          const totalBorrowed = req.jumlah || 0;
          const alreadyReturned = req.jumlahSudahDikembalikan || 0;
          const remainingBorrowed = totalBorrowed - alreadyReturned;
          return remainingBorrowed > 0; // Still has items to return
        }
        
        return false;
      });

      // Sort by tanggalPinjam descending (newest first)
      unreturned.sort((a, b) => {
        const timeA = a.tanggalPinjam?.seconds || 0;
        const timeB = b.tanggalPinjam?.seconds || 0;
        return timeB - timeA;
      });

      setUnreturnedRequests(unreturned);
    } catch (err) {
      console.error("Error fetching unreturned requests:", err);
      setError('Gagal memuat data alat yang belum dikembalikan.');
    } finally {
      setLoadingUnreturned(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  // Fetch unreturned requests when switching to tab 2
  useEffect(() => {
    if (tabValue === 2) {
      fetchUnreturnedRequests();
    }
  }, [tabValue]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewModeChange = (event, newMode) => {
    if (newMode !== null) {
      setViewMode(newMode);
      if (newMode === 'today') {
        fetchRequests();
      } else {
        fetchRequests(selectedDate);
      }
    }
  };

  const handleDateChange = (event) => {
    const newDate = event.target.value;
    setSelectedDate(newDate);
    if (viewMode === 'history') {
      fetchRequests(newDate);
    }
  };

  const handleSubmissionExpand = (submissionId) => {
    setExpandedSubmission(expandedSubmission === submissionId ? null : submissionId);
  };
  
  // Handle approving all requests in a submission
  const handleApproveAllInSubmission = async (submissionId, requests) => {
    if (!window.confirm(`Apakah Anda yakin ingin menyetujui semua ${requests.length} permintaan dalam pengajuan ini?`)) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Process all requests in the submission
      for (const request of requests) {
        if (request.status === 'pending' || request.status === 'dosen_approved') {
          // Create a silent version of update status that doesn't set updatingId
          const requestRef = doc(db, 'permintaan_alat', request.id);
          const requestToUpdate = [...instrumentRequests, ...glasswareRequests].find(req => req.id === request.id);

          if (!requestToUpdate) {
            continue; // Skip if not found
          }

          const updateData = {
            status: 'approved',
            tanggalPersetujuanAdmin: Timestamp.now()
          };

          // Create approval history entry
          const approvalHistoryEntry = {
            approver: {
              id: auth.currentUser.uid,
              name: 'Admin',
              role: 'admin'
            },
            action: 'approved',
            timestamp: Timestamp.now()
          };

          // Update document with new status and append to approval history
          updateData.approvalHistory = requestToUpdate.approvalHistory 
            ? [...requestToUpdate.approvalHistory, approvalHistoryEntry]
            : [approvalHistoryEntry];

          await updateDoc(requestRef, updateData);

          if (requestToUpdate.jenisAlat === 'alat_gelas') {
            try {
              await updateAlatGelasStockOnLoanApproval(
                requestToUpdate.namaAlat,
                requestToUpdate.jumlah
              );
            } catch (stockError) {
              console.error(`Failed to update stock for ${requestToUpdate.namaAlat}:`, stockError);
              // Continue with other approvals even if stock update fails for one item
            }
          }
        }
      }
      
      // Refresh the list
      fetchRequests();
      setSuccess(`Berhasil menyetujui semua permintaan dalam pengajuan.`);
    } catch (err) {
      console.error("Error approving all requests:", err);
      setError(`Gagal menyetujui semua permintaan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle rejecting all requests in a submission
  const handleRejectAllInSubmission = async (submissionId, requests) => {
    if (!window.confirm(`Apakah Anda yakin ingin menolak semua ${requests.length} permintaan dalam pengajuan ini?`)) {
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      // Process all requests in the submission
      for (const request of requests) {
        if (request.status === 'pending' || request.status === 'dosen_approved') {
          // Create a silent version of update status that doesn't set updatingId
          const requestRef = doc(db, 'permintaan_alat', request.id);
          const requestToUpdate = [...instrumentRequests, ...glasswareRequests].find(req => req.id === request.id);

          if (!requestToUpdate) {
            continue; // Skip if not found
          }

          const updateData = {
            status: 'rejected',
            tanggalPersetujuanAdmin: Timestamp.now()
          };

          // Create approval history entry
          const approvalHistoryEntry = {
            approver: {
              id: auth.currentUser.uid,
              name: 'Admin',
              role: 'admin'
            },
            action: 'rejected',
            timestamp: Timestamp.now()
          };

          // Update document with new status and append to approval history
          updateData.approvalHistory = requestToUpdate.approvalHistory 
            ? [...requestToUpdate.approvalHistory, approvalHistoryEntry]
            : [approvalHistoryEntry];

          await updateDoc(requestRef, updateData);
        }
      }
      
      // Refresh the list
      fetchRequests();
      setSuccess(`Berhasil menolak semua permintaan dalam pengajuan.`);
    } catch (err) {
      console.error("Error rejecting all requests:", err);
      setError(`Gagal menolak semua permintaan: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenReturnDialog = async (request) => {
    console.log('=== Opening Return Dialog ===');
    console.log('Request clicked:', {
      id: request.id,
      userId: request.userId,
      namaUser: request.namaUser,
      jenisAlat: request.jenisAlat,
      status: request.status,
      submissionId: request.submissionId
    });
    
    // First, get all pending_return requests from the same student from current requests
    // Combine both instrument and glassware requests to get all requests
    // Only include requests that actually need return (pending_return status OR approved with partial return)
    const allRequests = [...instrumentRequests, ...glasswareRequests];
    let sameStudentRequests = allRequests.filter(req => {
      if (req.userId !== request.userId || req.jenisAlat !== request.jenisAlat) return false;
      
      // Tampilkan pending_return (sudah diajukan pengembalian)
      if (req.status === 'pending_return') return true;
      
      // Tampilkan semua approved/admin_approved yang belum dikembalikan sepenuhnya
      // Ini memastikan admin melihat semua riwayat peminjaman yang masih aktif
      if (req.status === 'approved' || req.status === 'admin_approved') {
        const totalBorrowed = req.jumlah || 0;
        const alreadyReturned = req.jumlahSudahDikembalikan || 0;
        const remainingBorrowed = totalBorrowed - alreadyReturned;
        // Tampilkan jika masih ada yang belum dikembalikan (termasuk yang belum pernah dikembalikan)
        return remainingBorrowed > 0;
      }
      
      return false;
    });
    
    console.log(`Found ${sameStudentRequests.length} pending_return requests in current view for ${request.namaUser || request.userId}`);
    console.log('Current view requests:', sameStudentRequests.map(r => ({
      id: r.id,
      namaAlat: r.namaAlat,
      submissionId: r.submissionId,
      tanggalPermintaan: r.tanggalPermintaan
    })));
    
    // Always fetch all pending_return requests for this student from database
    // to ensure we get all of them regardless of submission date, submissionId, or view mode
    try {
      const requestsRef = collection(db, 'permintaan_alat');
      // First, get all pending_return, approved, and admin_approved requests for this user (only 2 where clauses to avoid index issues)
      // We need to query statuses separately since Firestore doesn't support 'in' with multiple where clauses easily
      const userPendingReturnQuery = query(
        requestsRef,
        where('userId', '==', request.userId),
        where('status', '==', 'pending_return')
      );
      const userApprovedQuery = query(
        requestsRef,
        where('userId', '==', request.userId),
        where('status', '==', 'approved')
      );
      const userAdminApprovedQuery = query(
        requestsRef,
        where('userId', '==', request.userId),
        where('status', '==', 'admin_approved')
      );
      
      const [userPendingReturnSnapshot, userApprovedSnapshot, userAdminApprovedSnapshot] = await Promise.all([
        getDocs(userPendingReturnQuery),
        getDocs(userApprovedQuery),
        getDocs(userAdminApprovedQuery)
      ]);
      
      console.log(`Database query returned ${userPendingReturnSnapshot.size} pending_return, ${userApprovedSnapshot.size} approved, and ${userAdminApprovedSnapshot.size} admin_approved for user ${request.userId}`);
      
      // Combine and filter client-side by jenisAlat and check for remaining borrowed amount
      // Only include approved requests that already have some returned amount (partial return)
      const allPendingReturnRequests = [
        ...userPendingReturnSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })),
        ...userApprovedSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() })),
        ...userAdminApprovedSnapshot.docs.map(docSnap => ({ id: docSnap.id, ...docSnap.data() }))
      ].filter(req => {
        if (req.jenisAlat !== request.jenisAlat) return false;
        
        // Include pending_return (sudah diajukan pengembalian)
        if (req.status === 'pending_return') return true;
        
        // Include semua approved/admin_approved yang belum dikembalikan sepenuhnya
        // Ini memastikan admin melihat semua riwayat peminjaman yang masih aktif
        if (req.status === 'approved' || req.status === 'admin_approved') {
          const totalBorrowed = req.jumlah || 0;
          const alreadyReturned = req.jumlahSudahDikembalikan || 0;
          const remainingBorrowed = totalBorrowed - alreadyReturned;
          // Tampilkan jika masih ada yang belum dikembalikan (termasuk yang belum pernah dikembalikan)
          return remainingBorrowed > 0;
        }
        
        return false;
      });
      
      console.log(`After filtering by jenisAlat (${request.jenisAlat}): ${allPendingReturnRequests.length} requests`);
      console.log('Database requests:', allPendingReturnRequests.map(r => ({
        id: r.id,
        namaAlat: r.namaAlat,
        submissionId: r.submissionId,
        tanggalPermintaan: r.tanggalPermintaan,
        jenisAlat: r.jenisAlat
      })));
      
      // Merge with current requests, avoiding duplicates
      const existingIds = new Set(sameStudentRequests.map(req => req.id));
      const additionalRequests = allPendingReturnRequests.filter(req => !existingIds.has(req.id));
      console.log(`Found ${additionalRequests.length} additional requests not in current view`);
      sameStudentRequests = [...sameStudentRequests, ...additionalRequests];
      
      console.log(`Total pending_return requests after merge: ${sameStudentRequests.length}`);
      console.log('Final requests:', sameStudentRequests.map(r => ({
        id: r.id,
        namaAlat: r.namaAlat,
        submissionId: r.submissionId
      })));
    } catch (err) {
      console.error('Error fetching all pending_return requests:', err);
      // Continue with what we have from current requests
    }
    
    // Ensure we have at least the current request
    if (sameStudentRequests.length === 0) {
      console.warn('No requests found, using clicked request only');
      sameStudentRequests = [request];
    }
    
    setReturnRequests(sameStudentRequests);
    
    // Initialize quantities for all requests
    // If partially returned, default to remaining amount, otherwise to total
    const initialQuantities = {};
    sameStudentRequests.forEach(req => {
      const totalBorrowed = req.jumlah || 0;
      const alreadyReturned = req.jumlahSudahDikembalikan || 0;
      const remainingBorrowed = totalBorrowed - alreadyReturned;
      // Default to remaining amount if partially returned, otherwise to total
      initialQuantities[req.id] = req.jumlahDikembalikan || (remainingBorrowed > 0 ? remainingBorrowed : totalBorrowed);
    });
    setReturnQuantities(initialQuantities);
    setReturnDialogOpen(true);
    console.log('=== Return Dialog Opened ===');
  };

  const handleCloseReturnDialog = () => {
    setReturnDialogOpen(false);
    setReturnRequests([]);
    setReturnQuantities({});
  };

  const handleQuantityChange = (requestId, value) => {
    const numValue = Number(value);
    if (!isNaN(numValue) && numValue >= 0) {
      setReturnQuantities({ ...returnQuantities, [requestId]: numValue });
    }
  };

  const handleShowStudentDetails = (request) => {
    setSelectedStudent(request);
    setStudentDetailsOpen(true);
  };

  const handleApproveReturn = async (request) => {
    setUpdatingId(request.id);
    setError('');
    try {
      // Get returned quantity from state - must be explicitly set by user
      let returnedQuantity = returnQuantities[request.id];
      const totalBorrowed = request.jumlah || 0;
      const alreadyReturned = request.jumlahSudahDikembalikan || 0;
      const remainingBorrowed = totalBorrowed - alreadyReturned;
      
      // Validate that returned quantity is set
      if (returnedQuantity === undefined || returnedQuantity === null || isNaN(returnedQuantity)) {
        throw new Error('Silakan masukkan jumlah yang akan dikembalikan');
      }
      
      const totalReturned = alreadyReturned + returnedQuantity;
      const finalRemainingBorrowed = totalBorrowed - totalReturned;
      
      // Validate returned quantity
      if (returnedQuantity <= 0) {
        throw new Error('Jumlah dikembalikan harus lebih dari 0');
      }
      if (returnedQuantity > remainingBorrowed) {
        throw new Error(`Jumlah dikembalikan (${returnedQuantity}) tidak boleh melebihi jumlah yang masih dipinjam (${remainingBorrowed})`);
      }
      
      // Update stock for alat gelas
      if (request.jenisAlat === 'alat_gelas') {
      await updateAlatGelasStockOnReturn(
        request.namaAlat,
        returnedQuantity,
        request.alatId // Pass alatId if available for more reliable lookup
      );
      }

      // Determine status based on whether all items are returned
      const isFullyReturned = totalReturned >= totalBorrowed;
      const newStatus = isFullyReturned ? 'returned' : 'approved';

      const updateData = {
        status: newStatus,
        jumlahSudahDikembalikan: totalReturned,
        jumlahMasihDipinjam: finalRemainingBorrowed,
        jumlahDikembalikanActual: returnedQuantity, // Last return amount
        tanggalDikembalikanActual: Timestamp.now(),
        catatanPengembalian: !isFullyReturned 
          ? `Pengembalian sebagian: ${returnedQuantity} dari ${totalBorrowed} (${alreadyReturned} sudah dikembalikan sebelumnya, ${finalRemainingBorrowed} masih dipinjam)`
          : returnedQuantity !== totalBorrowed
            ? `Jumlah dikembalikan berbeda dari jumlah dipinjam (${totalBorrowed})`
          : null
      };

      // Add to approval history
      const historyEntry = {
        status: isFullyReturned ? 'returned' : 'partially_returned',
            timestamp: Timestamp.now(),
        returnedQuantity: returnedQuantity,
        totalReturned: totalReturned,
        remainingBorrowed: remainingBorrowed,
            approver: {
              uid: auth.currentUser.uid,
              role: 'admin'
            }
      };

      if (request.approvalHistory) {
        updateData.approvalHistory = [...request.approvalHistory, historyEntry];
      } else {
        updateData.approvalHistory = [historyEntry];
      }

      await updateDoc(doc(db, 'permintaan_alat', request.id), updateData);

      // Clean up active_instrument_usage only if fully returned (for instruments)
      if (request.jenisAlat === 'instrumen' && request.alatId && isFullyReturned) {
        try {
          const usageQuery = query(
            collection(db, 'active_instrument_usage'),
            where('alatId', '==', request.alatId),
            where('userId', '==', request.userId)
          );
          const usageSnapshot = await getDocs(usageQuery);
          if (!usageSnapshot.empty) {
            // Delete all active usage entries for this user and alat
            const batch = writeBatch(db);
            usageSnapshot.docs.forEach(usageDoc => {
              batch.delete(usageDoc.ref);
            });
            await batch.commit();
            console.log('Cleaned up active_instrument_usage for returned request');
          }
        } catch (cleanupError) {
          console.error('Error cleaning up active_instrument_usage:', cleanupError);
          // Don't throw - this is cleanup, not critical
        }
      }

      handleCloseReturnDialog();
      // Refresh with current view mode and date
      if (viewMode === 'today') {
        fetchRequests();
      } else {
        fetchRequests(selectedDate);
      }
      
      // Also refresh unreturned requests if on tab 2
      if (tabValue === 2) {
        fetchUnreturnedRequests();
      }
      
      if (isFullyReturned) {
        setSuccess(`Berhasil menyetujui pengembalian lengkap untuk ${request.namaAlat}.`);
      } else {
        setSuccess(`Berhasil menyetujui pengembalian sebagian: ${returnedQuantity} dari ${totalBorrowed}. Masih ${finalRemainingBorrowed} yang dipinjam.`);
      }
    } catch (err) {
      console.error('Error approving return:', err);
      setError(`Gagal menyetujui pengembalian: ${err.message}`);
    } finally {
      setUpdatingId(null);
    }
  };

  // Handle approving all returns for the same student
  const handleApproveAllReturns = async () => {
    if (returnRequests.length === 0) return;
    
    setUpdatingId('all');
    setError('');
    try {
      const batch = writeBatch(db);
      
      for (const request of returnRequests) {
        // Get returned quantity from state, don't use fallback to request.jumlah
        // If not in state, calculate from remaining borrowed amount
        let returnedQuantity = returnQuantities[request.id];
        const totalBorrowed = request.jumlah || 0;
        const alreadyReturned = request.jumlahSudahDikembalikan || 0;
        const remainingBorrowed = totalBorrowed - alreadyReturned;
        
        // If returnedQuantity is not set or invalid, use remaining amount as default
        if (returnedQuantity === undefined || returnedQuantity === null || isNaN(returnedQuantity)) {
          returnedQuantity = remainingBorrowed > 0 ? remainingBorrowed : totalBorrowed;
        }
        
        const totalReturned = alreadyReturned + returnedQuantity;
        const finalRemainingBorrowed = totalBorrowed - totalReturned;
        
        // Validate returned quantity
        if (returnedQuantity <= 0) {
          console.warn(`Invalid return quantity for request ${request.id}: ${returnedQuantity}`);
          continue;
        }
        if (returnedQuantity > remainingBorrowed) {
          console.warn(`Return quantity (${returnedQuantity}) exceeds remaining (${remainingBorrowed}) for request ${request.id}`);
          continue;
        }
        
        // Update stock for alat gelas
        if (request.jenisAlat === 'alat_gelas') {
          await updateAlatGelasStockOnReturn(request.namaAlat, returnedQuantity, request.alatId);
        }

        // Determine status based on whether all items are returned
        const isFullyReturned = totalReturned >= totalBorrowed;
        const newStatus = isFullyReturned ? 'returned' : 'approved';

        const updateData = {
          status: newStatus,
          jumlahSudahDikembalikan: totalReturned,
          jumlahMasihDipinjam: finalRemainingBorrowed,
          jumlahDikembalikanActual: returnedQuantity,
          tanggalDikembalikanActual: Timestamp.now(),
          catatanPengembalian: !isFullyReturned 
            ? `Pengembalian sebagian: ${returnedQuantity} dari ${totalBorrowed} (${alreadyReturned} sudah dikembalikan sebelumnya, ${finalRemainingBorrowed} masih dipinjam)`
            : returnedQuantity !== totalBorrowed
              ? `Jumlah dikembalikan berbeda dari jumlah dipinjam (${totalBorrowed})`
              : null
        };

        // Add to approval history
        const historyEntry = {
          status: isFullyReturned ? 'returned' : 'partially_returned',
          timestamp: Timestamp.now(),
          returnedQuantity: returnedQuantity,
          totalReturned: totalReturned,
          remainingBorrowed: remainingBorrowed,
          approver: {
            uid: auth.currentUser.uid,
            role: 'admin'
          }
        };

        if (request.approvalHistory) {
          updateData.approvalHistory = [...request.approvalHistory, historyEntry];
        } else {
          updateData.approvalHistory = [historyEntry];
        }

        const requestRef = doc(db, 'permintaan_alat', request.id);
        batch.update(requestRef, updateData);

        // Clean up active_instrument_usage only if fully returned (for instruments)
        if (request.jenisAlat === 'instrumen' && request.alatId && isFullyReturned) {
          try {
            const usageQuery = query(
              collection(db, 'active_instrument_usage'),
              where('alatId', '==', request.alatId),
              where('userId', '==', request.userId)
            );
            const usageSnapshot = await getDocs(usageQuery);
            if (!usageSnapshot.empty) {
              usageSnapshot.docs.forEach(usageDoc => {
                batch.delete(usageDoc.ref);
              });
            }
          } catch (cleanupError) {
            console.error('Error cleaning up active_instrument_usage:', cleanupError);
          }
        }
      }

      await batch.commit();

      handleCloseReturnDialog();
      // Refresh with current view mode and date
      if (viewMode === 'today') {
      fetchRequests();
      } else {
        fetchRequests(selectedDate);
      }
      
      // Also refresh unreturned requests if on tab 2
      if (tabValue === 2) {
        fetchUnreturnedRequests();
      }
      
      setSuccess(`Berhasil menyetujui ${returnRequests.length} pengembalian alat.`);
    } catch (err) {
      console.error('Error approving all returns:', err);
      setError(`Gagal menyetujui pengembalian: ${err.message}`);
    } finally {
      setUpdatingId(null);
    }
  };

  const TabPanel = (props) => {
    const { children, value, index, ...other } = props;
    return (
      <div role="tabpanel" hidden={value !== index} {...other}>
        {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
      </div>
    );
  };

  // Helper function to render status badge
  const renderStatusBadge = (status) => (
    <span className={`status-badge status-${status}`}>
      {status === 'dosen_approved' ? 'Disetujui Dosen' :
       status === 'dosen_rejected' ? 'Ditolak Dosen' :
       status === 'admin_approved' ? 'Disetujui Admin' :
       status === 'admin_rejected' ? 'Ditolak Admin' :
       status === 'pending' ? 'Menunggu Persetujuan' :
       status === 'approved' ? 'Disetujui' :
       status === 'rejected' ? 'Ditolak' :
       status === 'cancelled' ? 'Dibatalkan' :
       status === 'returned' ? 'Dikembalikan' :
       status === 'pending_return' ? 'Menunggu Pengembalian' :
       status || 'Unknown'}
    </span>
  );

  // Render action buttons for a request
  // showReturnButton: only show return button on first request of same student with pending_return
  const renderActionButtons = (req, onUpdateStatus, onDelete, updatingId, onOpenReturnDialog, showReturnButton = false) => (
    <Box sx={{ display: 'flex', gap: 1 }}>
      {(req.status === 'pending' || req.status === 'dosen_approved') && (
        <>
          <Button
            variant="contained"
            color="success"
            size="small"
            onClick={() => onUpdateStatus(req.id, 'approved')}
            disabled={updatingId === req.id}
          >
            {updatingId === req.id ? '...' : 'Setujui'}
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            onClick={() => onUpdateStatus(req.id, 'rejected')}
            disabled={updatingId === req.id}
          >
            {updatingId === req.id ? '...' : 'Tolak'}
          </Button>
        </>
      )}
      {req.status === 'approved' && (
        <Button
          variant="contained"
          color="warning"
          size="small"
          onClick={() => onUpdateStatus(req.id, 'cancelled')}
          disabled={updatingId === req.id}
        >
          {updatingId === req.id ? '...' : 'Batalkan'}
        </Button>
      )}
      <Button
        variant="contained"
        color="error"
        size="small"
        onClick={() => onDelete(req.id)}
        disabled={updatingId === req.id}
      >
        {updatingId === req.id ? '...' : 'Hapus'}
      </Button>
      {req.status === 'pending_return' && showReturnButton && (
        <Button
          variant="contained"
          color="primary"
          size="small"
          onClick={() => onOpenReturnDialog(req)}
          disabled={updatingId === req.id}
        >
          Periksa Pengembalian
        </Button>
      )}
    </Box>
  );

  // Helper to truncate submission ID to max 5 characters
  const truncateSubmissionId = (submissionId, tanggalPermintaan = null) => {
    if (!submissionId) {
      // For legacy requests without submissionId, show formatted date if available
      if (tanggalPermintaan) {
        try {
          const date = tanggalPermintaan.toDate ? tanggalPermintaan.toDate() : new Date(tanggalPermintaan);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear().toString().substring(2);
          return `${day}/${month}/${year}`;
        } catch (e) {
          return 'Lama';
        }
      }
      return 'Lama';
    }
    // If it contains underscore, take the part after the first underscore (seconds part)
    // Format: userId_seconds_nanoseconds_random -> show seconds part
    if (submissionId.includes('_')) {
      const parts = submissionId.split('_');
      // For old format: userId_seconds -> show seconds
      // For new format: userId_seconds_nanoseconds_random -> show seconds
      if (parts.length >= 2) {
      return parts[1].substring(0, 5);
      }
    }
    // Otherwise just take the first 5 characters
    return submissionId.substring(0, 5);
  };

  // Unified RequestTable component for both instrument and glassware
  const RequestTable = ({ requests, onUpdateStatus, onDelete, updatingId, handleShowStudentDetails }) => {
    // Group requests by submission ID first
    const groupedBySubmission = groupRequestsBySubmission(requests);
    
    // Helper function to get date key (YYYY-MM-DD) from timestamp
    const getDateKey = (timestamp) => {
      if (!timestamp) return null;
      try {
        const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (e) {
        return null;
      }
    };
    
    // For alat gelas with pending return, group by userId instead of submissionId
    // For instruments, group by userId + date to show all requests from same user on same day
    const groupedRequests = {};
    const userGroupsForReturn = {}; // Track users that need return grouping (alat gelas)
    const userGroupsForInstruments = {}; // Track users that need grouping by date (instruments)
    
    // First pass: identify users with alat gelas that need return AND instruments by date
    Object.values(groupedBySubmission).forEach(submissionData => {
      const firstRequest = submissionData.requests[0];
      if (!firstRequest) return;
      
      const userId = firstRequest.userId;
      const dateKey = getDateKey(submissionData.tanggalPermintaan);
      
      // Check for alat gelas with pending return
      const hasPendingReturn = submissionData.requests.some(req => {
        if (req.jenisAlat !== 'alat_gelas') return false;
        if (req.status === 'pending_return') return true;
        if (req.status === 'approved' || req.status === 'admin_approved') {
          const totalBorrowed = req.jumlah || 0;
          const alreadyReturned = req.jumlahSudahDikembalikan || 0;
          const remainingBorrowed = totalBorrowed - alreadyReturned;
          return remainingBorrowed > 0;
        }
        return false;
      });
      
      // Check if all requests in this submission are instruments
      const allInstruments = submissionData.requests.every(req => req.jenisAlat === 'instrumen');
      
      // Group alat gelas with pending return by userId
      if (hasPendingReturn && userId) {
        if (!userGroupsForReturn[userId]) {
          userGroupsForReturn[userId] = {
            namaUser: submissionData.namaUser,
            userNim: submissionData.userNim,
            submissionId: null, // Combined group, no single submissionId
            tanggalPermintaan: submissionData.tanggalPermintaan, // Use first submission date
            requests: []
          };
        }
        // Add all requests from this submission to the user group
        userGroupsForReturn[userId].requests.push(...submissionData.requests);
        // Update tanggalPermintaan to the earliest one
        if (submissionData.tanggalPermintaan?.seconds < (userGroupsForReturn[userId].tanggalPermintaan?.seconds || Infinity)) {
          userGroupsForReturn[userId].tanggalPermintaan = submissionData.tanggalPermintaan;
        }
      }
      
      // Group instruments by userId + date
      if (allInstruments && userId && dateKey) {
        const groupKey = `${userId}_${dateKey}`;
        if (!userGroupsForInstruments[groupKey]) {
          userGroupsForInstruments[groupKey] = {
            namaUser: submissionData.namaUser,
            userNim: submissionData.userNim,
            submissionId: null, // Combined group, no single submissionId
            tanggalPermintaan: submissionData.tanggalPermintaan,
            requests: []
          };
        }
        // Add all requests from this submission to the user+date group
        userGroupsForInstruments[groupKey].requests.push(...submissionData.requests);
        // Update tanggalPermintaan to the earliest one
        if (submissionData.tanggalPermintaan?.seconds < (userGroupsForInstruments[groupKey].tanggalPermintaan?.seconds || Infinity)) {
          userGroupsForInstruments[groupKey].tanggalPermintaan = submissionData.tanggalPermintaan;
        }
      }
    });
    
    // Second pass: build final grouped requests
    Object.entries(groupedBySubmission).forEach(([submissionId, submissionData]) => {
      const firstRequest = submissionData.requests[0];
      if (!firstRequest) return;
      
      const userId = firstRequest.userId;
      const dateKey = getDateKey(submissionData.tanggalPermintaan);
      const allInstruments = submissionData.requests.every(req => req.jenisAlat === 'instrumen');
      
      // Check for alat gelas with pending return
      const hasPendingReturn = submissionData.requests.some(req => {
        if (req.jenisAlat !== 'alat_gelas') return false;
        if (req.status === 'pending_return') return true;
        if (req.status === 'approved' || req.status === 'admin_approved') {
          const totalBorrowed = req.jumlah || 0;
          const alreadyReturned = req.jumlahSudahDikembalikan || 0;
          const remainingBorrowed = totalBorrowed - alreadyReturned;
          return remainingBorrowed > 0;
        }
        return false;
      });
      
      // Skip if already grouped
      if (hasPendingReturn && userId && userGroupsForReturn[userId]) {
        // Skip individual submissions, will add combined group later
        return;
      } else if (allInstruments && userId && dateKey && userGroupsForInstruments[`${userId}_${dateKey}`]) {
        // Skip individual instrument submissions, will add combined group later
        return;
      } else {
        // Keep original submission grouping for other cases
        groupedRequests[submissionId] = submissionData;
      }
    });
    
    // Add combined user groups for return (alat gelas)
    Object.entries(userGroupsForReturn).forEach(([userId, userGroup]) => {
      groupedRequests[`user_${userId}_return`] = userGroup;
    });
    
    // Add combined user groups for instruments (by date)
    Object.entries(userGroupsForInstruments).forEach(([groupKey, userGroup]) => {
      groupedRequests[`user_${groupKey}_instruments`] = userGroup;
    });
    
    // Also group pending_return requests by userId + jenisAlat for batch return button
    // Only include requests that actually need return (pending_return status OR approved with partial return)
    const pendingReturnByStudent = {};
    requests.forEach(req => {
      let shouldInclude = false;
      
      // Include pending_return (sudah diajukan pengembalian)
      if (req.status === 'pending_return') {
        shouldInclude = true;
      } else if (req.status === 'approved' || req.status === 'admin_approved') {
        // Include semua approved yang belum dikembalikan sepenuhnya
        // Ini memastikan admin melihat semua riwayat peminjaman yang masih aktif
        const totalBorrowed = req.jumlah || 0;
        const alreadyReturned = req.jumlahSudahDikembalikan || 0;
        const remainingBorrowed = totalBorrowed - alreadyReturned;
        // Tampilkan jika masih ada yang belum dikembalikan (termasuk yang belum pernah dikembalikan)
        shouldInclude = remainingBorrowed > 0;
      }
      
      if (shouldInclude) {
        const key = `${req.userId}_${req.jenisAlat}`;
        if (!pendingReturnByStudent[key]) {
          pendingReturnByStudent[key] = {
            userId: req.userId,
            namaUser: req.namaUser,
            jenisAlat: req.jenisAlat,
            requests: []
          };
        }
        pendingReturnByStudent[key].requests.push(req);
      }
    });
    
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        <table className="persetujuan-alat-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Nama Peminjam</th>
              <th>Nama Alat</th>
              <th>Jml</th>
              <th>Tgl Pinjam</th>
              <th>Tgl Selesai</th>
              <th>Tujuan</th>
              <th>Dosen Terkait</th>
              <th>Status</th>
              <th>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {Object.entries(groupedRequests).map(([submissionId, submissionData]) => {
              // Check if this submission has pending_return requests (only for alat gelas, not instruments)
              const hasPendingReturn = submissionData.requests.some(req => {
                // Only alat gelas can have pending return
                if (req.jenisAlat !== 'alat_gelas') return false;
                if (req.status === 'pending_return') return true;
                if (req.status === 'approved' || req.status === 'admin_approved') {
                  const totalBorrowed = req.jumlah || 0;
                  const alreadyReturned = req.jumlahSudahDikembalikan || 0;
                  const remainingBorrowed = totalBorrowed - alreadyReturned;
                  // Tampilkan jika masih ada yang belum dikembalikan (termasuk yang belum pernah dikembalikan)
                  return remainingBorrowed > 0;
                }
                return false;
              });
              
              // Find first pending_return request for return button (only alat gelas)
              const firstPendingReturnRequest = submissionData.requests.find(req => {
                // Only alat gelas can have pending return
                if (req.jenisAlat !== 'alat_gelas') return false;
                if (req.status === 'pending_return') return true;
                if (req.status === 'approved' || req.status === 'admin_approved') {
                  const totalBorrowed = req.jumlah || 0;
                  const alreadyReturned = req.jumlahSudahDikembalikan || 0;
                  const remainingBorrowed = totalBorrowed - alreadyReturned;
                  // Tampilkan jika masih ada yang belum dikembalikan (termasuk yang belum pernah dikembalikan)
                  return remainingBorrowed > 0;
                }
                return false;
              });
              
              // Count all alat gelas requests from the same user that need return (across all submissions in current view)
              // This ensures the count matches what will be shown in the dialog
              const userId = submissionData.requests[0]?.userId;
              const pendingReturnCount = requests.filter(req => {
                // Only count requests from the same user and same jenisAlat
                if (req.userId !== userId || req.jenisAlat !== 'alat_gelas') return false;
                if (req.status === 'pending_return') return true;
                if (req.status === 'approved' || req.status === 'admin_approved') {
                  const totalBorrowed = req.jumlah || 0;
                  const alreadyReturned = req.jumlahSudahDikembalikan || 0;
                  const remainingBorrowed = totalBorrowed - alreadyReturned;
                  // Tampilkan jika masih ada yang belum dikembalikan (termasuk yang belum pernah dikembalikan)
                  return remainingBorrowed > 0;
                }
                return false;
              }).length;
              
              return (
              <React.Fragment key={submissionId}>
                {/* Add a row for batch approval if there are pending items */}
                {submissionData.requests.some(req => req.status === 'pending' || req.status === 'dosen_approved') && (
                  <tr className="batch-approval-row">
                    <td colSpan="9" className="batch-approval-cell">
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        ID: {truncateSubmissionId(submissionData.submissionId, submissionData.tanggalPermintaan)} - {submissionData.namaUser} ({submissionData.requests.length} alat)
                      </Typography>
                    </td>
                    <td className="action-buttons">
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          color="success"
                          size="small"
                          onClick={() => handleApproveAllInSubmission(submissionId, submissionData.requests)}
                          disabled={loading}
                        >
                          Setujui Semua
                        </Button>
                        <Button
                          variant="contained"
                          color="error"
                          size="small"
                          onClick={() => handleRejectAllInSubmission(submissionId, submissionData.requests)}
                          disabled={loading}
                        >
                          Tolak Semua
                        </Button>
                      </Box>
                    </td>
                  </tr>
                )}
                  
                  {/* Add return button in the same row as student name if has pending return */}
                  {hasPendingReturn && !submissionData.requests.some(req => req.status === 'pending' || req.status === 'dosen_approved') && (
                    <tr className="batch-approval-row" style={{ backgroundColor: '#f3e5f5' }}>
                      <td colSpan="9" className="batch-approval-cell">
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {submissionData.submissionId ? `ID: ${truncateSubmissionId(submissionData.submissionId, submissionData.tanggalPermintaan)} - ` : ''}{submissionData.namaUser} ({submissionData.requests.length} alat)
                        </Typography>
                      </td>
                      <td className="action-buttons">
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {firstPendingReturnRequest && (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleOpenReturnDialog(firstPendingReturnRequest)}
                              disabled={loading || updatingId === 'all'}
                            >
                              Periksa Pengembalian ({pendingReturnCount})
                            </Button>
                          )}
                        </Box>
                      </td>
                    </tr>
                  )}
                  
                  {/* Add return button to existing batch approval row if both exist */}
                  {submissionData.requests.some(req => req.status === 'pending' || req.status === 'dosen_approved') && hasPendingReturn && (
                    <tr className="batch-approval-row" style={{ backgroundColor: '#f3e5f5' }}>
                      <td colSpan="9" className="batch-approval-cell">
                        <Typography variant="body2" sx={{ fontWeight: 'bold', color: '#7b1fa2' }}>
                          {submissionData.namaUser} - {pendingReturnCount} alat menunggu pengembalian
                        </Typography>
                      </td>
                      <td className="action-buttons">
                        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                          {firstPendingReturnRequest && (
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleOpenReturnDialog(firstPendingReturnRequest)}
                              disabled={loading || updatingId === 'all'}
                            >
                              Periksa Pengembalian ({pendingReturnCount})
                            </Button>
                          )}
                      </Box>
                    </td>
                  </tr>
                )}
                
                {/* Individual item rows */}
                {submissionData.requests.map((req, index) => (
                  <tr 
                    key={req.id} 
                    style={index === 0 ? { borderTop: '1px solid #eee' } : {}}
                    onClick={() => handleShowStudentDetails(req)}
                    className="clickable-row compact-row"
                  >
                    {index === 0 ? (
                      <td rowSpan={submissionData.requests.length}>
                        {submissionData.submissionId ? truncateSubmissionId(submissionData.submissionId, submissionData.tanggalPermintaan) : '-'}
                      </td>
                    ) : null}
                    
                    {index === 0 ? (
                      <td rowSpan={submissionData.requests.length}>
                        {req.namaUser || 'N/A'}
                      </td>
                    ) : null}
                    
                    <td>{req.namaAlat || 'N/A'}</td>
                    <td>{req.jumlah || 'N/A'}</td>
                    <td>{formatDate(req.tanggalPinjam)}</td>
                    <td>{formatDate(req.tanggalKembali)}</td>
                    <td>{req.tujuanPenggunaan || 'N/A'}</td>
                    <td>{req.namaDosenTerkait || 'N/A'}</td>
                    <td>{renderStatusBadge(req.status)}</td>
                    <td className="action-buttons" onClick={(e) => e.stopPropagation()}>
                      {renderActionButtons(req, onUpdateStatus, onDelete, updatingId, handleOpenReturnDialog, false)}
                    </td>
                  </tr>
                ))}
              </React.Fragment>
              );
            })}
            
            {requests.length === 0 && (
              <tr>
                <td colSpan="10" style={{ textAlign: 'center' }}>
                  Tidak ada permintaan alat yang perlu diproses.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </Paper>
    );
  };

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h5" gutterBottom>
        Persetujuan Permintaan Alat
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {/* View Mode Toggle and Date Picker */}
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={handleViewModeChange}
          aria-label="view mode"
          size="small"
        >
          <ToggleButton value="today" aria-label="today">
            Hari Ini
          </ToggleButton>
          <ToggleButton value="history" aria-label="history">
            Riwayat
          </ToggleButton>
        </ToggleButtonGroup>
        
        {viewMode === 'history' && (
          <TextField
            type="date"
            label="Pilih Tanggal"
            value={selectedDate}
            onChange={handleDateChange}
            size="small"
            InputLabelProps={{ shrink: true }}
            sx={{ minWidth: 200 }}
          />
        )}
      </Box>

      {loading ? (
        <Typography>Memuat data permintaan...</Typography>
      ) : (
        <>
          <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
            <Tab label="Instrumen" />
            <Tab label="Alat Gelas" />
            <Tab label="Alat Belum Dikembalikan" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Typography variant="h6" gutterBottom>Permintaan Instrumen</Typography>
            <RequestTable
              requests={instrumentRequests}
              onUpdateStatus={handleUpdateRequestStatus}
              onDelete={handleDeleteRequest}
              updatingId={updatingId}
              handleShowStudentDetails={handleShowStudentDetails}
            />
          </TabPanel>

          {/* Student Details Dialog */}
          <Dialog 
            open={studentDetailsOpen} 
            onClose={() => {
              setStudentDetailsOpen(false);
              setSelectedStudent(null);
            }}
            maxWidth="md"
            fullWidth
          >
            <DialogTitle>Detail Mahasiswa</DialogTitle>
            <DialogContent>
              {selectedStudent && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Detail Mahasiswa
                  </Typography>
                  <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '24px' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Nama</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{selectedStudent.namaUser}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>NIM</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{selectedStudent.userNim || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Dosen Terkait</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{selectedStudent.namaDosenTerkait || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Tujuan Penggunaan</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{selectedStudent.tujuanPenggunaan || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Tanggal Pinjam</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{formatDate(selectedStudent.tanggalPinjam)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Tanggal Kembali</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{formatDate(selectedStudent.tanggalKembali)}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Status</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
                          <span className={`status-badge status-${selectedStudent.status}`}>
                            {selectedStudent.status === 'dosen_approved' ? 'Disetujui Dosen' :
                             selectedStudent.status === 'dosen_rejected' ? 'Ditolak Dosen' :
                             selectedStudent.status === 'admin_approved' ? 'Disetujui Admin' :
                             selectedStudent.status === 'admin_rejected' ? 'Ditolak Admin' :
                             selectedStudent.status === 'pending' ? 'Menunggu Persetujuan' :
                             selectedStudent.status === 'approved' ? 'Disetujui' :
                             selectedStudent.status === 'rejected' ? 'Ditolak' :
                             selectedStudent.status === 'cancelled' ? 'Dibatalkan' :
                             selectedStudent.status === 'returned' ? 'Dikembalikan' :
                             selectedStudent.status === 'pending_return' ? 'Menunggu Pengembalian' :
                             selectedStudent.status || 'Unknown'}
                          </span>
                        </td>
                      </tr>
                      {selectedStudent.approvalHistory && selectedStudent.approvalHistory.length > 0 && (
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Riwayat Persetujuan</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>
                            {selectedStudent.approvalHistory.map((history, index) => (
                              <div key={index} style={{ marginBottom: '8px' }}>
                                <span className={`status-badge status-${history.status}`}>
                                  {history.status === 'dosen_approved' ? 'Disetujui Dosen' :
                                   history.status === 'dosen_rejected' ? 'Ditolak Dosen' :
                                   history.status === 'admin_approved' ? 'Disetujui Admin' :
                                   history.status === 'admin_rejected' ? 'Ditolak Admin' :
                                   history.status}
                                </span>
                                <br />
                                <small style={{ color: '#666' }}>
                                  {history.approver.name} ({history.approver.role}) - {history.action === 'approved' ? 'Menyetujui' : 'Menolak'} pada {formatDate(history.timestamp)}
                                </small>
                              </div>
                            ))}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>

                  {/* Detail Alat Section */}
                  <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Detail Alat
                  </Typography>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <tbody>
                      <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Nama Alat</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{selectedStudent.namaAlat}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Jumlah</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{selectedStudent.jumlah}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Spesifikasi</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{selectedStudent.spesifikasi || 'N/A'}</td>
                      </tr>
                      <tr>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Lokasi</td>
                        <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{selectedStudent.lokasi || 'N/A'}</td>
                      </tr>
                      {selectedStudent.catatanPengembalian && (
                        <tr>
                          <td style={{ padding: '8px', borderBottom: '1px solid #ddd', fontWeight: 'bold' }}>Catatan Pengembalian</td>
                          <td style={{ padding: '8px', borderBottom: '1px solid #ddd' }}>{selectedStudent.catatanPengembalian}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => {
                setStudentDetailsOpen(false);
                setSelectedStudent(null);
              }}>
                Tutup
              </Button>
            </DialogActions>
          </Dialog>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h6" gutterBottom>Permintaan Alat Gelas</Typography>
            <RequestTable
              requests={glasswareRequests}
              onUpdateStatus={handleUpdateRequestStatus}
              onDelete={handleDeleteRequest}
              updatingId={updatingId}
              handleShowStudentDetails={handleShowStudentDetails}
            />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" gutterBottom>
                Alat Gelas yang Belum Dikembalikan ({unreturnedRequests.length})
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={fetchUnreturnedRequests}
                disabled={loadingUnreturned}
              >
                {loadingUnreturned ? 'Memuat...' : 'Refresh'}
              </Button>
            </Box>
            {loadingUnreturned ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 3 }}>
                <CircularProgress />
              </Box>
            ) : (
              <RequestTable
                requests={unreturnedRequests}
                onUpdateStatus={handleUpdateRequestStatus}
                onDelete={handleDeleteRequest}
                updatingId={updatingId}
                handleShowStudentDetails={handleShowStudentDetails}
              />
            )}
          </TabPanel>

          {/* Return Approval Dialog */}
          <Dialog open={returnDialogOpen} onClose={handleCloseReturnDialog} maxWidth="md" fullWidth>
            <DialogTitle>Persetujuan Pengembalian Alat</DialogTitle>
            <DialogContent>
              {returnRequests.length > 0 && (
                <>
                  <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
                    Mahasiswa: {returnRequests[0].namaUser} ({returnRequests[0].userNim || 'N/A'})
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Nama Alat</strong></TableCell>
                        <TableCell align="right"><strong>Dipinjam</strong></TableCell>
                        <TableCell align="right"><strong>Sudah Dikembalikan</strong></TableCell>
                        <TableCell align="right"><strong>Masih Dipinjam</strong></TableCell>
                        <TableCell align="right"><strong>Dikembalikan Sekarang</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {returnRequests.map((request) => {
                        const totalBorrowed = request.jumlah || 0;
                        const alreadyReturned = request.jumlahSudahDikembalikan || 0;
                        const remainingBorrowed = totalBorrowed - alreadyReturned;
                        const maxReturn = remainingBorrowed > 0 ? remainingBorrowed : totalBorrowed;
                        
                        return (
                          <TableRow key={request.id}>
                            <TableCell>{request.namaAlat}</TableCell>
                            <TableCell align="right">{totalBorrowed}</TableCell>
                            <TableCell align="right">
                              {alreadyReturned > 0 ? (
                                <Typography variant="body2" color="success.main">
                                  {alreadyReturned}
                  </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  0
                      </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                              {remainingBorrowed > 0 ? (
                                <Typography variant="body2" color="warning.main" fontWeight="bold">
                                  {remainingBorrowed}
                                </Typography>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  0
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell align="right">
                  <TextField
                    type="number"
                                value={returnQuantities[request.id] || ''}
                                onChange={(e) => handleQuantityChange(request.id, e.target.value)}
                                size="small"
                                inputProps={{ 
                                  min: 0, 
                                  max: maxReturn, 
                                  style: { textAlign: 'right' } 
                                }}
                                sx={{ width: 100 }}
                                helperText={alreadyReturned > 0 ? `Maks: ${maxReturn}` : ''}
                              />
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseReturnDialog}>Batal</Button>
              <Button
                onClick={handleApproveAllReturns}
                color="primary"
                variant="contained"
                disabled={updatingId === 'all' || returnRequests.length === 0}
              >
                {updatingId === 'all' ? 'Memproses...' : `Setujui Semua (${returnRequests.length})`}
              </Button>
            </DialogActions>
          </Dialog>
        </>
      )}
    </Box>
  );
};

export default PersetujuanPermintaanAlat;
